import GroupedSearchCard from "./GroupedSearchCard";
import SearchBar from "./SearchBar";
import SearchCard from "./SearchCard";
import { groupSubmissionsByRenewalHistory } from "./SearchDialog.support";
import { DATE_MODE, FILTER_MODE, SORT_ORDER } from "./constants";
import { isPlainTextSearch, splitClientOnlyTokens } from "./searchSupport";
import {
  AddRounded as AddIcon,
  FolderOpen as FolderIcon,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Fab,
  Link,
  Stack,
  Typography,
  Tooltip,
} from "@mui/material";
import { hasValue } from "common/strings";
import DefaultLoader from "components/common/DefaultLoader";
import OfflineBanner from "components/common/OfflineBanner";
import SubmissionFileUploadDrawer from "components/common/SubmissionFileUploadDrawer";
import Visible from "components/common/Visible";
import Dashboard from "components/layout/Dashboard";
import { SUPPORT_EMAIL } from "config";
import * as people from "domain/people";
import {
  SEARCH_CREATE_FROM_NO_MATCHES,
  SUBMISSION_FILE_UPLOAD_DRAWER,
} from "flags";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import * as staticDataService from "services/staticDataService";
import * as submissionsService from "services/submissionsService";
import * as persistenceActions from "store/actions/persistence/persistenceActions";
import useDebounceCallback from "store/hooks/useDebounceCallback";
import * as userSelectors from "store/selectors/user/userSelectors";

const CREATE_CHECK = {
  IDLE: "IDLE",
  CHECKING: "CHECKING",
  HIDDEN_MATCHES: "HIDDEN_MATCHES",
};

const HIDDEN_MATCH_LIMIT = 10;

const describeHiddenMatches = (count) => {
  if (count >= HIDDEN_MATCH_LIMIT) {
    return `${HIDDEN_MATCH_LIMIT}+ matches`;
  }
  return count === 1 ? "1 match" : `${count} matches`;
};

const CreateFromSearch = ({ createFromSearch }) => {
  const {
    status,
    hiddenMatchCount,
    creating,
    searchPending,
    onCreate,
    onCreateAnyway,
    onShowAll,
  } = createFromSearch;
  if (status === CREATE_CHECK.HIDDEN_MATCHES) {
    return (
      <Stack className={"fade-in-slow"} alignItems={"center"} spacing={1}>
        <Typography variant={"body2"}>
          {`Current filters are hiding ${describeHiddenMatches(
            hiddenMatchCount
          )}`}
        </Typography>
        <Stack direction={"row"} spacing={1}>
          <Button
            variant={"outlined"}
            onClick={onShowAll}
            disabled={creating}
            sx={{ borderRadius: 9999, paddingX: 2.5 }}
            data-testid={"search-show-matches"}
          >
            {"Show Matches"}
          </Button>
          <Button
            variant={"contained"}
            disableElevation
            onClick={onCreateAnyway}
            loading={creating}
            sx={{ borderRadius: 9999, paddingX: 2.5 }}
            data-testid={"search-create-anyway"}
          >
            {"Create anyway"}
          </Button>
        </Stack>
      </Stack>
    );
  }
  return (
    <Button
      variant={"contained"}
      disableElevation
      startIcon={<AddIcon />}
      loading={status === CREATE_CHECK.CHECKING || creating}
      loadingPosition={"start"}
      disabled={searchPending}
      onClick={onCreate}
      sx={{ borderRadius: 9999, paddingX: 2.5 }}
      data-testid={"search-create-submission"}
    >
      {"Create submission"}
    </Button>
  );
};

const NoMatches = ({ searchText, createFromSearch }) => {
  const label =
    "No matches found" + (searchText === "" ? "" : ` for '${searchText}'`);
  return (
    <Stack
      className={"search-result inert"}
      justifyContent={"center"}
      alignItems={"center"}
      spacing={2}
    >
      <span>{label}</span>
      {createFromSearch && (
        <CreateFromSearch createFromSearch={createFromSearch} />
      )}
    </Stack>
  );
};

const Error = () => {
  const mailLink =
    "mailto:" + SUPPORT_EMAIL + "?subject=Error%20Loading%20Search";
  return (
    <>
      <OfflineBanner />
      <Stack
        className={"search-result fade-in"}
        justifyContent={"center"}
        alignItems={"center"}
        sx={{
          height: "220px",
          cursor: "default !important",
          boxShadow: "none !important",
        }}
      >
        <Box sx={{ maxWidth: "30ch", textAlign: "center" }}>
          <Typography variant={"subtitle1"}>
            {"Sorry, there was a problem loading data from Marmalade"}
          </Typography>
          <Typography
            variant={"subtitle1"}
            sx={{ marginTop: "1rem !important" }}
          >
            {
              "Please try refreshing the page in a moment or feel free to contact "
            }
            <Link href={mailLink}>{"support"}</Link>
          </Typography>
        </Box>
      </Stack>
    </>
  );
};

const AccessForbidden = () => {
  const mailLink =
    "mailto:" +
    SUPPORT_EMAIL +
    "?subject=Insufficient%20Privilege%20Loading%20Search";
  return (
    <>
      <OfflineBanner />
      <Stack
        className={"search-result fade-in"}
        justifyContent={"center"}
        alignItems={"center"}
        sx={{
          height: "220px",
          cursor: "default !important",
          boxShadow: "none !important",
        }}
      >
        <Box sx={{ maxWidth: "30ch", textAlign: "center" }}>
          <Typography variant={"subtitle1"}>
            {
              "Sorry, you do not have the required permissions to access this data"
            }
          </Typography>
          <Typography variant={"subtitle1"}>
            {"If you have recently been allocated access " +
              "you may need to log out and back in. "}
          </Typography>
          <Typography
            variant={"subtitle1"}
            sx={{ marginTop: "1rem !important" }}
          >
            {"If you still do not have access please feel free to contact "}
            <Link href={mailLink}>{"support"}</Link>
          </Typography>
        </Box>
      </Stack>
    </>
  );
};

const SubmissionList = ({
  submissions,
  searchConfig,
  configLoading,
  showNoMatches,
  showError,
  searchText,
  createFromSearch,
  filterClicked,
  isForbidden,
  renewalData,
  useRenewalGrouping,
  isLoadingMore,
  hasMoreResults,
  onLoadMore,
  submissionsFetching,
  submissionsLoading,
  renewalDataFetching,
}) => {
  const scrollContainerRef = useRef();
  const [isNearBottom, setIsNearBottom] = useState(false);

  const handleScroll = useCallback(() => {
    if (!scrollContainerRef.current) return;

    const {
      scrollTop,
      scrollHeight,
      clientHeight,
    } = scrollContainerRef.current;
    const scrollPercentage = (scrollTop + clientHeight) / scrollHeight;

    setIsNearBottom(scrollPercentage > 0.8);

    if (!isLoadingMore && hasMoreResults && scrollPercentage > 0.9) {
      onLoadMore();
    }
  }, [isLoadingMore, hasMoreResults, onLoadMore]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener("scroll", handleScroll);
      return () => container.removeEventListener("scroll", handleScroll);
    }
  }, [handleScroll]);

  const showLoadingIndicator =
    isNearBottom &&
    isLoadingMore &&
    ((submissionsFetching && !submissionsLoading) || renewalDataFetching);

  const groupedSubmissions = useMemo(() => {
    if (!useRenewalGrouping) {
      return [];
    }
    return groupSubmissionsByRenewalHistory(submissions, renewalData);
  }, [useRenewalGrouping, submissions, renewalData]);

  if (!useRenewalGrouping) {
    return (
      <Box
        mt={1}
        mb={1}
        ref={scrollContainerRef}
        sx={{
          maxHeight: "calc(100vh - 200px)",
          overflowY: "auto",
          paddingBottom: 1,
        }}
      >
        {showNoMatches ? (
          <NoMatches
            searchText={searchText}
            createFromSearch={createFromSearch}
          />
        ) : showError ? (
          isForbidden ? (
            <AccessForbidden />
          ) : (
            <Error />
          )
        ) : (
          <div>
            {submissions?.map((submission) => (
              <SearchCard
                key={submission.submissionId}
                submission={submission}
                searchConfig={searchConfig}
                configLoading={configLoading}
                filterClicked={filterClicked}
                data-testid={`submission-${submission.insured}`}
              />
            ))}
            {showLoadingIndicator && (
              <Stack alignItems={"center"} margin={2}>
                <DefaultLoader type={"TailSpin"} size={24} />
                <Typography variant={"caption"} color={"secondary"} mt={1}>
                  {"Loading more submissions..."}
                </Typography>
              </Stack>
            )}
          </div>
        )}
      </Box>
    );
  }

  return (
    <Box
      mt={1}
      mb={1}
      ref={scrollContainerRef}
      sx={{
        maxHeight: "calc(100vh - 200px)",
        overflowY: "auto",
        paddingBottom: 1,
      }}
    >
      {showNoMatches ? (
        <NoMatches
          searchText={searchText}
          createFromSearch={createFromSearch}
        />
      ) : showError ? (
        isForbidden ? (
          <AccessForbidden />
        ) : (
          <Error />
        )
      ) : (
        <div>
          {groupedSubmissions.map((group) => (
            <GroupedSearchCard
              key={group.submissions[0]?.submissionId}
              group={group}
              searchConfig={searchConfig}
              configLoading={configLoading}
              filterClicked={filterClicked}
              data-testid={`submission-group-${
                group.groupName || group.submissions[0]?.insured
              }`}
            />
          ))}
          {showLoadingIndicator && (
            <Stack alignItems={"center"} margin={2}>
              <DefaultLoader type={"TailSpin"} size={24} />
              <Typography variant={"caption"} color={"secondary"} mt={1}>
                {"Loading more submissions..."}
              </Typography>
            </Stack>
          )}
        </div>
      )}
    </Box>
  );
};

const Loader = () => (
  <Stack alignItems={"center"} margin={2}>
    <DefaultLoader
      type={"TailSpin"}
      size={48}
      style={{ marginTop: "0.5rem" }}
    />
    <Typography className={"pulse"} color={"secondary"} mt={1}>
      {"Loading submissions data"}
    </Typography>
  </Stack>
);

const makeQuery = ({ query, filterMode, dateMode, user }) => {
  const makeFilterPrefix = (mode) => {
    if (mode?.startsWith?.("LOCATION:")) {
      return "";
    } else if (mode === FILTER_MODE.EVERYTHING) {
      return "";
    } else if (mode === FILTER_MODE.SAVED_BY_ME) {
      const email = user?.attributes?.email;
      return email ? `savedBy:"${email}"` : "";
    } else if (mode === FILTER_MODE.RENEWING_SOON) {
      return "isRenewingWithin3Mo:true";
    } else {
      return "";
    }
  };

  const makeDatePrefix = (mode) => {
    if (mode === DATE_MODE.EVERYTHING) {
      return "";
    } else if (mode === DATE_MODE.THIS_YEAR) {
      const currentYear = new Date().getFullYear().toString();
      return `inception:${currentYear}`;
    } else if (mode === DATE_MODE.LATEST) {
      return `isLatest:true`;
    } else {
      return "";
    }
  };

  return (
    makeFilterPrefix(filterMode) +
    " " +
    makeDatePrefix(dateMode) +
    " " +
    (query ?? "")
  );
};

const makeSort = (sort) => {
  if (sort === SORT_ORDER.DATE) {
    return "-savedAt";
  } else if (sort === SORT_ORDER.EFFECTIVE_DATE) {
    return "-effectiveDate";
  } else if (sort === SORT_ORDER.INSURED_NAME) {
    return "insured";
  } else {
    return null;
  }
};

const SearchDialog = () => {
  const navigate = useNavigate();

  const dispatch = useDispatch();

  const user = useSelector(userSelectors.selectUser);
  const isUserReadOnly = useSelector(userSelectors.isUserReadOnly);
  const isVisible = useSelector(userSelectors.selectIsVisible);
  // Opt-out: on unless a company or user config sets the tag to false.
  const createFromNoMatches = isVisible(SEARCH_CREATE_FROM_NO_MATCHES) ?? true;

  const searchBarRef = useRef();
  const [uploadDrawerOpen, setUploadDrawerOpen] = useState(false);
  const [cacheKey, setCacheKey] = useState(null);
  const [filter, setFilter] = useState(
    localStorage.getItem("SEARCH_BAR_FILTER") || FILTER_MODE.EVERYTHING
  );
  const [dateMode, setDateMode] = useState(
    localStorage.getItem("SEARCH_BAR_DATE_MODE") || DATE_MODE.EVERYTHING
  );
  const [sort, setSort] = useState(
    localStorage.getItem("SEARCH_BAR_SORT_ORDER") || SORT_ORDER.DATE
  );
  const [searchQuery, setDebouncedSearchQuery] = useState("");
  // True from a keystroke until the debounced query catches up. While it is
  // set, the results on screen are for older text, so creating from them
  // would use the wrong insured name.
  const [searchPending, setSearchPending] = useState(false);
  const settleSearch = useCallback((searchText) => {
    setDebouncedSearchQuery(searchText);
    setSearchPending(false);
  }, []);
  const { set: setSearchQuery } = useDebounceCallback(settleSearch);
  const [createCheck, setCreateCheck] = useState(CREATE_CHECK.IDLE);
  const [hiddenMatchCount, setHiddenMatchCount] = useState(0);
  const [creating, setCreating] = useState(false);
  const createCheckRunRef = useRef(0);
  const pendingCheckRef = useRef(null);
  const [
    querySubmissionsOnce,
  ] = submissionsService.useLazyQuerySubmissionsQuery();

  const [allSubmissions, setAllSubmissions] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [hasMoreResults, setHasMoreResults] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [accumulatedRenewalData, setAccumulatedRenewalData] = useState(null);
  const loadMoreInFlightRef = useRef(false);

  const pageSize = 30;
  const {
    backendQuery: searchQueryForBackend,
    clientTokens: clientSearchTokens,
  } = useMemo(() => splitClientOnlyTokens(searchQuery), [searchQuery]);

  const currentQueryParams = {
    query: makeQuery({
      query: searchQueryForBackend,
      filterMode: filter,
      dateMode,
      user,
    }),
    sort: makeSort(sort),
  };

  const {
    data: submissions,
    isLoading: submissionsLoading,
    isFetching: submissionsFetching,
    isError,
    error,
  } = submissionsService.useQuerySubmissionsQuery(
    {
      cacheKey,
      query: currentQueryParams.query,
      sort: currentQueryParams.sort,
      limit: pageSize,
      skip: currentPage * pageSize,
    },
    { skip: !cacheKey }
  );

  const forbidden = error?.name === "FORBIDDEN";

  const {
    data: searchConfig,
    isLoading: searchConfigLoading,
  } = staticDataService.useConfigQuery("submission_search_config");

  const {
    data: underwritersConfig,
    isLoading: underwritersLoading,
  } = staticDataService.useConfigQuery("underwriters");

  const lastSearchConfigRef = useRef(null);
  const lastUnderwritersAllRef = useRef(null);

  useEffect(() => {
    if (searchConfig != null) {
      lastSearchConfigRef.current = searchConfig;
    }
  }, [searchConfig]);

  useEffect(() => {
    if (underwritersConfig?.all?.length) {
      lastUnderwritersAllRef.current = underwritersConfig.all;
    }
  }, [underwritersConfig]);

  const effectiveSearchConfig =
    searchConfig ?? lastSearchConfigRef.current ?? null;

  const underwritersAllForEnrichment =
    underwritersConfig?.all?.length > 0
      ? underwritersConfig.all
      : lastUnderwritersAllRef.current;

  const locationConfig = effectiveSearchConfig?.location_config ?? null;

  const blockSearchOnConfig =
    (searchConfigLoading && effectiveSearchConfig == null) ||
    (locationConfig != null &&
      underwritersLoading &&
      !underwritersAllForEnrichment?.length);

  const currentSubmissionIds = submissions?.map((s) => s.submissionId) || [];

  const {
    data: renewalData,
    isFetching: renewalDataFetching,
  } = submissionsService.useListRenewalSubmissionsQuery(
    { ids: currentSubmissionIds },
    { skip: currentSubmissionIds.length === 0 }
  );

  const useRenewalGrouping = staticDataService.useFeatureFlag(
    "use-renewal-grouping-in-search",
    {
      default: false,
    }
  );

  // Location filters are applied client-side only — they don't change the
  // backend query, so switching between LOCATION:* values (or back to
  // EVERYTHING) must NOT clear allSubmissions. Derive a key that only
  // changes when the *backend* filter actually changes.
  const backendFilterKey = filter?.startsWith?.("LOCATION:")
    ? FILTER_MODE.EVERYTHING
    : filter;

  useEffect(() => {
    setAllSubmissions([]);
    setCurrentPage(0);
    setHasMoreResults(true);
    setIsLoadingMore(false);
    loadMoreInFlightRef.current = false;
    setAccumulatedRenewalData(null);
  }, [searchQueryForBackend, backendFilterKey, dateMode, sort, cacheKey]);

  useEffect(() => {
    if (submissions && !submissionsLoading) {
      if (currentPage === 0) {
        setAllSubmissions(submissions);
      } else {
        setAllSubmissions((prev) => {
          const existingIds = new Set(prev.map((s) => s.submissionId));
          const newSubmissions = submissions.filter(
            (s) => !existingIds.has(s.submissionId)
          );
          return [...prev, ...newSubmissions];
        });
      }

      setHasMoreResults(submissions.length === pageSize);
      setIsLoadingMore(false);
      loadMoreInFlightRef.current = false;
    }
  }, [submissions, submissionsLoading, currentPage, pageSize]);

  useEffect(() => {
    if (renewalData) {
      setAccumulatedRenewalData((prevData) => {
        if (!prevData) return renewalData;

        const mergedRenewalMap = {
          ...prevData.renewalMap,
          ...renewalData.renewalMap,
        };

        const mergedSubmissions = {
          ...prevData.submissions,
          ...renewalData.submissions,
        };

        return {
          renewalMap: mergedRenewalMap,
          submissions: mergedSubmissions,
        };
      });
    }
  }, [renewalData]);

  const loadMoreSubmissions = useCallback(() => {
    if (loadMoreInFlightRef.current) {
      return;
    }
    if (
      !isLoadingMore &&
      hasMoreResults &&
      !submissionsLoading &&
      !submissionsFetching
    ) {
      loadMoreInFlightRef.current = true;
      setIsLoadingMore(true);
      setCurrentPage((prev) => prev + 1);
    }
  }, [isLoadingMore, hasMoreResults, submissionsLoading, submissionsFetching]);

  useEffect(() => {
    searchBarRef.current.focus();
  }, []);

  useEffect(() => {
    if (cacheKey == null) {
      const timestamp = new Date().toISOString();
      setCacheKey(timestamp);
    }
  }, [cacheKey, setCacheKey]);

  const handleNewSubmission = () => {
    dispatch(persistenceActions.newSubmission({ navigate }));
  };

  // Bumping the run makes any in-flight check ignore its result, so it can
  // no longer create a submission; aborting just stops the request early.
  const cancelPendingCheck = useCallback(() => {
    createCheckRunRef.current += 1;
    pendingCheckRef.current?.abort?.();
    pendingCheckRef.current = null;
  }, []);

  // A new search or filter starts any pending create-from-search check over.
  useEffect(() => {
    cancelPendingCheck();
    setCreateCheck(CREATE_CHECK.IDLE);
  }, [cancelPendingCheck, searchQuery, searchPending, filter, dateMode]);

  // Leaving the search page (e.g. via the + button) must not let a check
  // that is still running create a submission over whatever is open next.
  useEffect(() => cancelPendingCheck, [cancelPendingCheck]);

  const createFromSearchText = async () => {
    setCreating(true);
    try {
      // Resolves once the submission has loaded and navigation has started;
      // reset afterwards so a failed create leaves the button usable.
      await dispatch(
        persistenceActions.newSubmission({ navigate, insured: searchQuery })
      );
    } finally {
      setCreating(false);
    }
  };

  const handleCreateFromSearch = async () => {
    const filtersActive =
      filter !== FILTER_MODE.EVERYTHING || dateMode !== DATE_MODE.EVERYTHING;
    if (!filtersActive) {
      createFromSearchText();
      return;
    }
    // The empty results may only be empty because of the filters, so check
    // the unfiltered search once before creating what may be a duplicate.
    const run = ++createCheckRunRef.current;
    setCreateCheck(CREATE_CHECK.CHECKING);
    let hiddenMatches = 0;
    try {
      // The backend filters its whole cached list either way, so the limit
      // only caps how much comes back; past it the count reads "10+".
      const request = querySubmissionsOnce({
        cacheKey,
        query: searchQuery,
        sort: makeSort(sort),
        limit: HIDDEN_MATCH_LIMIT,
        skip: 0,
      });
      pendingCheckRef.current = request;
      const unfiltered = await request.unwrap();
      hiddenMatches = unfiltered?.length ?? 0;
    } catch (e) {
      // The check only guards against duplicates, so a failed check should
      // not stop the user creating the submission they asked for. A
      // cancelled check also lands here and is stopped by the run guard.
    }
    if (run !== createCheckRunRef.current) {
      return;
    }
    pendingCheckRef.current = null;
    if (hiddenMatches > 0) {
      setHiddenMatchCount(hiddenMatches);
      setCreateCheck(CREATE_CHECK.HIDDEN_MATCHES);
    } else {
      setCreateCheck(CREATE_CHECK.IDLE);
      createFromSearchText();
    }
  };

  const enrichedSubmissions = useMemo(() => {
    const list = underwritersAllForEnrichment;
    if (!list?.length || !allSubmissions?.length) {
      return allSubmissions;
    }
    return allSubmissions.map((submission) => {
      const existing = submission.primaryUnderwriterLocation;
      if (hasValue(existing) && existing !== "-") {
        return submission;
      }
      const preferredUw = people.extractPreferredUnderwriterFromProgram(
        submission.people
      );
      if (!preferredUw) {
        return submission;
      }
      const byId =
        preferredUw.id != null
          ? list.find((u) => u.id === preferredUw.id)
          : null;
      const byEmail =
        preferredUw.email != null
          ? list.find(
              (u) =>
                u.email != null &&
                String(u.email).toLowerCase() ===
                  String(preferredUw.email).toLowerCase()
            )
          : null;
      const uw = byId ?? byEmail;
      const uwLocation = uw?.location != null ? String(uw.location).trim() : "";
      if (!uwLocation) {
        return submission;
      }
      return { ...submission, primaryUnderwriterLocation: uwLocation };
    });
  }, [allSubmissions, underwritersAllForEnrichment]);

  const activeLocationFilter = useMemo(() => {
    if (!filter?.startsWith("LOCATION:") || !locationConfig) {
      return null;
    }
    const label = filter.slice("LOCATION:".length);
    const option = locationConfig.locationDropdownOptions?.find(
      (opt) => opt.label === label
    );
    return option?.locations ?? null;
  }, [filter, locationConfig]);

  const displaySubmissions = useMemo(() => {
    let result = enrichedSubmissions;
    if (activeLocationFilter?.length) {
      result = result.filter((s) =>
        activeLocationFilter.includes(s.primaryUnderwriterLocation)
      );
    }
    if (clientSearchTokens.length > 0) {
      result = result.filter((s) =>
        clientSearchTokens.every(({ field, value }) => {
          const fieldValue = String(s?.[field] ?? "").toLowerCase();
          return fieldValue.includes(value);
        })
      );
    }
    return result;
  }, [enrichedSubmissions, activeLocationFilter, clientSearchTokens]);

  const showNoMatches =
    !submissionsLoading &&
    !submissionsFetching &&
    (hasValue(searchQuery) ||
      dateMode !== DATE_MODE.EVERYTHING ||
      activeLocationFilter != null) &&
    displaySubmissions?.length === 0 &&
    currentPage === 0;

  const appendSearch = (text) => {
    text = text.trim();
    if (!searchQuery.toLowerCase().includes(text.toLowerCase())) {
      const newSearchQuery = (searchQuery + " " + text).trim();
      searchBarRef.current.updateSearchText(newSearchQuery);
    } else {
      const newSearchQuery = searchQuery
        .split(text)
        .map((query) => query.trim())
        .join(" ");
      searchBarRef.current.updateSearchText(newSearchQuery);
    }
  };

  const updateDateMode = (mode) => {
    if (mode === DATE_MODE.LATEST) {
      const currentYear = new Date().getFullYear().toString();
      if (searchQuery.includes(currentYear)) {
        const newSearchQuery = searchQuery.replace(currentYear, "").trim();
        searchBarRef.current.updateSearchText(newSearchQuery);
      }
    }
    setDateMode(mode);
  };

  const changeFilter = (key) => {
    localStorage.setItem("SEARCH_BAR_FILTER", key);
    setFilter(key);
  };

  const changeDateMode = (mode) => {
    localStorage.setItem("SEARCH_BAR_DATE_MODE", mode);
    updateDateMode(mode);
  };

  const createFromSearch =
    createFromNoMatches && !isUserReadOnly && isPlainTextSearch(searchQuery)
      ? {
          status: createCheck,
          hiddenMatchCount,
          creating,
          searchPending,
          onCreate: handleCreateFromSearch,
          onCreateAnyway: createFromSearchText,
          onShowAll: () => {
            changeFilter(FILTER_MODE.EVERYTHING);
            changeDateMode(DATE_MODE.EVERYTHING);
          },
        }
      : null;

  return (
    <Dashboard>
      <div className={"search-dialog"}>
        <div className={"search-form"}>
          <div className={"results"}>
            <SearchBar
              ref={searchBarRef}
              onChangeSearch={(searchText) => {
                setSearchPending(true);
                setSearchQuery(searchText);
              }}
              activeFilter={filter}
              onChangeFilter={changeFilter}
              locationConfig={locationConfig}
              activeDateMode={dateMode}
              onChangeDateMode={changeDateMode}
              activeSort={sort}
              onChangeSort={(key) => {
                localStorage.setItem("SEARCH_BAR_SORT_ORDER", key);
                setSort(key);
              }}
              isFetching={
                (submissionsFetching && !submissionsLoading) ||
                renewalDataFetching
              }
              searchConfig={searchConfig}
            />
            {submissionsLoading || blockSearchOnConfig ? (
              <Loader />
            ) : (
              <SubmissionList
                submissions={displaySubmissions}
                searchConfig={effectiveSearchConfig}
                configLoading={false}
                showNoMatches={showNoMatches}
                showError={isError}
                isForbidden={forbidden}
                searchText={searchQuery}
                createFromSearch={createFromSearch}
                filterClicked={(text) => appendSearch(text)}
                renewalData={accumulatedRenewalData || renewalData}
                useRenewalGrouping={useRenewalGrouping}
                isLoadingMore={isLoadingMore}
                hasMoreResults={hasMoreResults}
                onLoadMore={loadMoreSubmissions}
                submissionsFetching={submissionsFetching}
                submissionsLoading={submissionsLoading}
                renewalDataFetching={renewalDataFetching}
                currentPage={currentPage}
              />
            )}
          </div>
        </div>
        {!isUserReadOnly && (
          <>
            <Fab
              className={"fade-in-slow"}
              onClick={handleNewSubmission}
              color={"primary"}
              sx={{ position: "fixed", right: "1.5rem", bottom: "1.5rem" }}
              data-testid={"new-submission"}
            >
              <AddIcon sx={{ color: "white", fontSize: "2.5rem" }} />
            </Fab>
            <Visible
              byTag={SUBMISSION_FILE_UPLOAD_DRAWER}
              defaultVisible={false}
            >
              <Tooltip title={"Upload Files"}>
                <Fab
                  className={"fade-in-slow"}
                  onClick={() => setUploadDrawerOpen(true)}
                  color={"secondary"}
                  sx={{ position: "fixed", right: "1.5rem", bottom: "5.5rem" }}
                  data-testid={"upload-files"}
                >
                  <FolderIcon sx={{ color: "white", fontSize: "2rem" }} />
                </Fab>
              </Tooltip>
              <SubmissionFileUploadDrawer
                open={uploadDrawerOpen}
                onClose={() => setUploadDrawerOpen(false)}
              />
            </Visible>
          </>
        )}
      </div>
    </Dashboard>
  );
};

export default SearchDialog;
